import { Box, Typography } from '@mui/material';
import { Button } from 'design-system';
import { useRef, useCallback } from 'react';
import { MdAddCircle } from 'react-icons/md';
import useFileCompression from 'hooks/useFileCompression';
import { SnackbarVariant, useSnackbar } from 'hooks/useSnackBar';
import { ACCEPTED_FILE_TYPES, isAcceptedUpload, rejectedUploadMessage } from 'utils/acceptedUpload';
import { sanitizeFileType } from 'utils/sanitizeFileType';
import { FormType } from 'generated/graphql';
import DragableFileEdits from './DragableFileEdits';

const FileUploader = ({
  label,
  files = [],
  onChange,
  isQCStorage = false,
  formType,
  id,
  disabled = false,
  uploadFolder,
  acceptedFileTypes = ACCEPTED_FILE_TYPES,
}: {
  label: string;
  files?: string[];
  onChange: (urls: string[]) => void;
  isQCStorage?: boolean;
  formType: FormType;
  id: string;
  disabled?: boolean;
  uploadFolder?: string;
  acceptedFileTypes?: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [compressedFile, compressFile] = useFileCompression();
  const { showSnackbar } = useSnackbar();

  const handleFileUpload = async (event: any) => {
    if (!event.target.files || !event.target.files.length) {
      compressFile(null);
      return;
    }
    if (!isAcceptedUpload(event.target.files[0], acceptedFileTypes)) {
      showSnackbar({ message: rejectedUploadMessage(acceptedFileTypes) }, SnackbarVariant.ERROR);
      event.target.value = '';
      return;
    }
    const sanitizedValue = await sanitizeFileType(event.target.files[0]);
    compressFile(sanitizedValue);
  };

  const handleAddClicked = () => {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.click();
  };

  const handleAddComplete = useCallback(
    (url: string) => {
      onChange([...files, url]);
      compressFile(null);
    },
    [files, onChange, compressFile],
  );

  const handleChange = (newSrc: string, oldSrc: string) => {
    onChange([...files.map((src) => (src === oldSrc ? newSrc : src))]);
  };

  const handleRemove = (removedUrl: string) => {
    onChange([...files.filter((src) => src !== removedUrl)]);
  };

  const handleDragAndDrop = (newOrder: string[]) => {
    onChange(newOrder);
  };

  return (
    <Box sx={(theme) => ({ marginBottom: theme.spacing(2) })}>
      <label htmlFor={id}>
        <input
          accept={acceptedFileTypes}
          type="file"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          ref={inputRef}
          id={id}
          disabled={disabled}
        />
        <Typography variant="h6" gutterBottom>
          {label}
        </Typography>
      </label>
      <DragableFileEdits
        onChange={handleChange}
        onRemove={handleRemove}
        onDragAndDrop={handleDragAndDrop}
        compressedFile={compressedFile}
        onComplete={handleAddComplete}
        isQCStorage={isQCStorage}
        uploadFolder={uploadFolder}
        acceptedFileTypes={acceptedFileTypes}
      >
        {files}
      </DragableFileEdits>
      {![FormType.ShipmentReceiveWithNotes].includes(formType) && (
        <Button variant="outlined" onClick={handleAddClicked} startIcon={<MdAddCircle size={20} />} disabled={disabled}>
          Add file
        </Button>
      )}
    </Box>
  );
};

export default FileUploader;
